




































































































import { Component, Prop, Ref, Watch, Vue } from 'vue-property-decorator'
import ECGChart from '@/components/ECGTab/ECGChart.vue'
import { ECG_DATA_LENGTH, ECG_SAVED_DATA_LENGTH } from '@/constants/ecg'

type ECGRecord = {
  id: number
  registeredDatetime: string
  measureDatetime: string
  heartWave: string
  heartRates: Array<{
    id: number
    registeredDatetime: string
    measureDatetime: string
    heartRate: string
  }>
}

@Component({
  components: {
    ECGChart
  },
  filters: {
    date(v: string) {
      return v.substr(0, 19)
    }
  }
})
export default class ECGTab extends Vue {
  @Prop({ default: null, type: Number }) year!: number | null
  @Ref('graph') graph_area!: HTMLDivElement
  @Ref('loadChart') loadChart!: ECGChart
  @Ref('savedChart') savedChart!: ECGChart
  history: ECGRecord[] = []
  unsubscribe?: Function
  type = 'load'

  zoom_ratio = 1.0
  zoom_ratio_options = [1.0, 2.0, 4.0]

  get userInfo() {
    return this.$store.getters['user/userInfo']
  }

  get relativeInfo() {
    return this.$store.getters['user/relativeInfo']
  }

  get patientId(): number | null | undefined {
    return this.userInfo.isPatientRelative
      ? this.relativeInfo?.patient.id
      : this.userInfo.id
  }

  get battery() {
    return this.$store.state.ecg.battery
  }

  get currentPr() {
    const pr = this.$store.getters['ecg/currentPr']
    return pr === -1 ? '−' : pr
  }

  get largeDataEnabled() {
    const config = this.$store.getters['appSettings/systemConfig'](
      'ecgLongSave'
    )
    return config === undefined ? true : config
  }

  @Watch('zoom_ratio')
  ratioChange() {
    if ((!this.largeDataEnabled || this.type === 'load') && this.loadChart) {
      this.$nextTick(() => {
        this.loadChart.resize()
      })
    }
    if (this.largeDataEnabled && this.type === 'saved' && this.savedChart) {
      this.$nextTick(() => {
        this.savedChart.resize()
      })
    }
  }

  @Watch('type')
  typeChange() {
    this.ratioChange()
  }

  async saveData() {
    if (this.userInfo.isPatientRelative) {
      this.$store.dispatch('alert/showMessages', ['このボタンは無効です'])
      return
    }
    const patientId = this.patientId
    if (patientId == null) return
    await this.$store.dispatch('ecg/save', {
      patientId
    })
    await this.reload()
  }

  async created() {
    await this.reload()
  }

  async reload() {
    const patientId = this.patientId
    if (patientId == null) return
    const res = await this.$api.ecg.fetch({
      patientId
    })
    this.history = res.results
  }

  changeData(data: ECGRecord) {
    this.type = 'saved'
    const md = new Date(data.measureDatetime)
    const now = new Date()
    const heartWaves = data.heartWave.split(',').map(v => ({
      ecg: Number(v),
      measurementTime: md
    }))
    const dataLength = this.largeDataEnabled
      ? ECG_SAVED_DATA_LENGTH
      : ECG_DATA_LENGTH
    let savedData = heartWaves
    if (dataLength > heartWaves.length) {
      savedData = heartWaves.concat(
        [...Array(dataLength - heartWaves.length)].map(() => ({
          ecg: NaN,
          measurementTime: now
        }))
      )
    } else if (dataLength < heartWaves.length) {
      savedData = savedData.slice(0, dataLength)
    }
    this.$store.commit('ecg/setSavedData', {
      data: savedData
    })
    this.$store.commit(
      'ecg/setSavedPr',
      data.heartRates.length > 0 ? Number(data.heartRates[0].heartRate) : 0
    )
    this.$store.dispatch('ecg/changeType', { type: 'saved' })
    if (this.largeDataEnabled) {
      this.savedChart.update()
    } else {
      this.loadChart.update()
    }
  }

  changeType(type: string) {
    this.type = type
    this.$store.dispatch('ecg/changeType', { type })
    if (type === 'load') {
      this.loadChart.update()
    }
  }

  isMounted = false
  height = 0
  width = 0
  savedWidth = 0

  mounted() {
    this.height = document.body.clientHeight * 0.6
    this.width = this.graph_area.clientWidth
    this.savedWidth = this.width * 3.75

    this.isMounted = true
  }

  beforeDestroy() {
    this.$store.commit('ecg/reset')
    this.$store.dispatch('ecg/changeType', { type: 'load' })
  }
}
